import React, { useState, useRef, useCallback } from 'react';
import {
  Box, Button, Card, CardContent, Typography,
  CircularProgress, Alert, Chip, Divider
} from '@mui/material';
import {
  CameraAlt, CheckCircle, Help, Cancel, Pets
} from '@mui/icons-material';
import Webcam from 'react-webcam';
import { identifyDog } from '../api/client';

const STATUS_CONFIG = {
  auto_confirmed: { color: 'success', icon: <CheckCircle />, label: 'Match Confirmed' },
  needs_confirmation: { color: 'warning', icon: <Help />, label: 'Please Confirm' },
  no_match: { color: 'error', icon: <Cancel />, label: 'No Match Found' },
  no_enrolled_dogs: { color: 'info', icon: <Pets />, label: 'No Dogs Enrolled' },
};

export default function Identify() {
  const webcamRef = useRef(null);
  const [cameraActive, setCameraActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [capturedImage, setCapturedImage] = useState(null);

  const capture = useCallback(async () => {
    const imageSrc = webcamRef.current?.getScreenshot();
    if (!imageSrc) return;
    setCapturedImage(imageSrc);
    setCameraActive(false);
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const blob = await fetch(imageSrc).then(r => r.blob());
      const file = new File([blob], 'capture.jpg', { type: 'image/jpeg' });
      const res = await identifyDog(file);
      setResult(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Identification failed');
    } finally {
      setLoading(false);
    }
  }, [webcamRef]);

  const reset = () => {
    setResult(null);
    setCapturedImage(null);
    setError('');
    setCameraActive(false);
  };

  const statusConfig = result ? STATUS_CONFIG[result.status] : null;

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
        Identify Dog
      </Typography>

      {!cameraActive && !capturedImage && !result && (
        <Card sx={{ textAlign: 'center', p: 3 }}>
          <Pets sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Point the camera at the dog's face and capture a photo to identify them
          </Typography>
          <Button
            variant="contained" size="large"
            startIcon={<CameraAlt />}
            onClick={() => setCameraActive(true)}
            fullWidth
          >
            Open Camera
          </Button>
        </Card>
      )}

      {cameraActive && (
        <Box>
          <Webcam
            ref={webcamRef}
            screenshotFormat="image/jpeg"
            videoConstraints={{ facingMode: 'environment' }}
            style={{ width: '100%', borderRadius: 12 }}
          />
          <Button
            variant="contained" size="large" fullWidth
            onClick={capture} sx={{ mt: 2 }}
            startIcon={<CameraAlt />}
          >
            Capture & Identify
          </Button>
          <Button fullWidth onClick={() => setCameraActive(false)} sx={{ mt: 1 }}>
            Cancel
          </Button>
        </Box>
      )}

      {capturedImage && !result && (
        <Box sx={{ textAlign: 'center' }}>
          <img
            src={capturedImage} alt="Captured"
            style={{ width: '100%', borderRadius: 12,
              maxHeight: 300, objectFit: 'cover' }}
          />
          {loading && (
            <Box sx={{ mt: 2 }}>
              <CircularProgress />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Identifying dog...
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

      {result && (
        <Box>
          {capturedImage && (
            <img
              src={capturedImage} alt="Captured"
              style={{ width: '100%', borderRadius: 12,
                maxHeight: 200, objectFit: 'cover', marginBottom: 16 }}
            />
          )}
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Chip
                  icon={statusConfig?.icon}
                  label={statusConfig?.label}
                  color={statusConfig?.color}
                  variant="filled"
                />
                {result.confidence && (
                  <Chip
                    label={`${(result.confidence * 100).toFixed(1)}% confidence`}
                    variant="outlined" size="small"
                  />
                )}
              </Box>

              {result.status === 'auto_confirmed' && (
                <>
                  <Typography variant="h5" fontWeight={700}>{result.name}</Typography>
                  <Typography color="text.secondary">{result.breed}</Typography>
                  {result.owner && (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      Owner: {result.owner}
                    </Typography>
                  )}
                </>
              )}

              {result.status === 'needs_confirmation' && result.top_candidate && (
                <>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {result.message}
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Typography variant="h6" fontWeight={700}>
                    {result.top_candidate.name}
                  </Typography>
                  <Typography color="text.secondary">
                    {result.top_candidate.breed}
                  </Typography>
                  {result.top_candidate.owner && (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      Owner: {result.top_candidate.owner}
                    </Typography>
                  )}
                  <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                    <Button variant="contained" color="success" fullWidth>
                      Yes, this is correct
                    </Button>
                    <Button variant="outlined" color="error" fullWidth onClick={reset}>
                      No, try again
                    </Button>
                  </Box>
                </>
              )}

              {result.status === 'no_match' && (
                <Typography color="text.secondary">{result.message}</Typography>
              )}
            </CardContent>
          </Card>
          <Button fullWidth onClick={reset} sx={{ mt: 2 }}>
            Identify Another Dog
          </Button>
        </Box>
      )}
    </Box>
  );
}
